import { create } from 'zustand';

/**
 * Lightweight pub/sub for "the activity feed should refresh now."
 *
 * Any mutation that emits an audit event server-side (picklist adjust,
 * cancel, post, picker change, future module wirings…) calls `bump()`
 * after a successful response. `ActivityList` includes `version` in its
 * fetch effect dependencies, so the bump triggers an immediate refetch
 * — no polling, no SSE, no per-caller wiring.
 *
 * ⚠️⚠️ THE PARAGRAPH THAT USED TO SIT HERE IS NOW FALSE, AND IT WAS THE TRAP THIS FEATURE EXISTS TO
 * DEFUSE. It read: "the strict-atomicity guarantee on the audit write means by the time the mutation API
 * returns 200, the new activity row is already committed; the refetch will see it."
 *
 * That held while the activity row was written inside the mutation's own transaction. Since E5 it is
 * written LATER — the mutation stages a domain event, a relay turns it into a child outbox job, and the
 * activity consumer writes the row on the Sidefx lane. So `bump()` now fires BEFORE the row exists, and a
 * refetch triggered by it usually returns without it.
 *
 * A bump is therefore a HINT that something is coming, not a promise that it has arrived. The panel treats
 * it as one: it refetches immediately and then polls on a bounded schedule until a row appears that it had
 * not already seen (`lib/arrivalPoll.ts`). Anything else consuming this store needs the same assumption.
 */
interface ActivityRefreshState {
  version: number;
  bump: () => void;
  /**
   * A QUIET re-read of page 1 (owner 2026-10-08): the detail page's 30-second tick and tab return. Unlike `bump`, it
   * promises nothing is coming, so it starts no arrival poll and shows no arrival skeleton; the panel skips it while
   * a page-1 read is already running. New rows merge in at the top (the reducer never truncates what was loaded).
   */
  pulse: number;
  refresh: () => void;
  /**
   * A bump that shows NOTHING while it waits (owner 2026-10-08: "dont place a placeholder at all when posting"). It
   * polls for the new row exactly like `bump`, but the panel draws no arrival skeleton and no "Still recording" for
   * it. `quiet` is the latest bump's kind: a normal `bump` landing meanwhile shows its placeholder as usual.
   */
  quiet: boolean;
  bumpQuietly: () => void;
}

export const useActivityRefreshStore = create<ActivityRefreshState>()((set) => ({
  version: 0,
  bump: () => set((state) => ({ version: state.version + 1, quiet: false })),
  pulse: 0,
  refresh: () => set((state) => ({ pulse: state.pulse + 1 })),
  quiet: false,
  bumpQuietly: () => set((state) => ({ version: state.version + 1, quiet: true })),
}));
